// client/src/pages/Auth/Login.js
// Inloggningssida. Form med email/password mot AuthContext.login().
// Redirectar till intended destination eller / vid lyckad inloggning.
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Auth.css'; // We'll create this for auth styling

const Login = () => {
  // Auth context and navigation
  const { login, isAuthenticated, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const intendedDestination = location.state?.from?.pathname || '/';
      navigate(intendedDestination, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    setFormErrors({});
  }, [formData, clearError, error]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!formData.email) {
      errors.email = 'Email är obligatorisk';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Ange en giltig email-adress';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Lösenord är obligatoriskt';
    } else if (formData.password.length < 8) {
      errors.password = 'Lösenord måste vara minst 8 tecken';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await login({
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });

      if (result.success) {
        // Successful login - navigation will happen via useEffect
        console.log('✅ Login successful:', result.user.email);
      }
    } catch (loginError) {
      console.error('❌ Login failed:', loginError.message);
      // Error is handled by AuthContext and displayed via error state
    } finally {
      setIsSubmitting(false);
    }
  };

  // Demo account helper
  const handleDemoLogin = () => {
    setFormData({
      email: 'demo@healthdata.se',
      password: 'DemoPass123!'
    });
  };

  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        
        {/* Auth Card */}
        <div className="auth-card">
          
          {/* Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <span role="img" aria-label="Health">🏥</span>
              <h1>Health Data System</h1>
            </div>
            <h2>Logga in på ditt konto</h2>
            <p>Välkommen tillbaka! Ange dina uppgifter för att fortsätta.</p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="auth-error">
              <span className="error-icon">❌</span>
              <span className="error-message">{error}</span>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="auth-form">
            
            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                📧 Email-adress
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${formErrors.email ? 'error' : ''}`}
                placeholder="din.email@example.com"
                autoComplete="email"
                disabled={loading || isSubmitting}
              />
              {formErrors.email && (
                <span className="form-error">{formErrors.email}</span>
              )}
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                🔐 Lösenord
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.password ? 'error' : ''}`}
                  placeholder="Ange ditt lösenord"
                  autoComplete="current-password"
                  disabled={loading || isSubmitting}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading || isSubmitting}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {formErrors.password && (
                <span className="form-error">{formErrors.password}</span>
              )}
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <Link to="/forgot-password" className="forgot-password-link">
                Glömt lösenord?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`auth-submit-btn ${isSubmitting || loading ? 'loading' : ''}`}
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading ? (
                <>
                  <span className="loading-spinner">⏳</span>
                  Loggar in...
                </>
              ) : (
                <>
                  🔓 Logga in
                </>
              )}
            </button>

          </form>

          {/* Demo Account */}
          <div className="demo-section">
            <div className="divider">
              <span>eller</span>
            </div>
            <button
              type="button"
              className="demo-btn"
              onClick={handleDemoLogin}
              disabled={loading || isSubmitting}
            >
              🎯 Fyll i demo-uppgifter
            </button>
          </div>

          {/* Register Link */}
          <div className="auth-footer">
            <p>
              Har du inget konto än?{' '}
              <Link to="/register" className="auth-link">
                Registrera dig här
              </Link>
            </p>
          </div>

        </div>

        {/* Side Info */}
        <div className="auth-info">
          <h3>🔒 Säker inloggning</h3>
          <ul>
            <li>✅ Krypterad dataöverföring</li>
            <li>✅ JWT-baserad autentisering</li>
            <li>✅ Säker token-hantering</li>
            <li>✅ GDPR-kompatibel</li>
          </ul>
          
          <div className="features-preview">
            <h4>Tillgång till:</h4>
            <ul>
              <li>📊 Personlig hälsodashboard</li>
              <li>📈 Avancerad dataanalys</li>
              <li>🗺️ Geografisk visualisering</li>
              <li>📝 Symptomrapportering</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;